/**
 * Two-column Filters popover: dimensions on the left, values on the right.
 * The check button and dismissing the popover both apply the draft.
 */

import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import {
  FILTER_DIMENSION_KEYS,
  FILTER_VALUES_PAGE_SIZE,
  loadFilterDimensionValues,
  loadFilterDimensions,
  type FilterDimension,
  type FilterOption,
  type FilterValuesPage,
} from "../../../services/regulationFilterDimensionsService";
import {
  countFilterSelections,
  emptyFilterSelection,
  filterSelectionsEqual,
  mergeFilterOptions,
  orderFilterDimensions,
  pinSelectedOptions,
  visibleValueOptions,
  type FilterSelectionState,
} from "./regulationFilterDraft";
import { FilterCriteriaEditor } from "./FilterCriteriaEditor";
import {
  ApplyCheckIcon,
  ChevronDownIcon,
  ClearFilterIcon,
  InsightFilterIcon,
  InsightSearchIcon,
  InsightSettingsIcon,
  SaveChangesIcon,
  SaveNewIcon,
  SelectChevronIcon,
} from "./filterPanelIcons";
import {
  ManageSavedFiltersDialog,
  SaveFilterNameDialog,
  type SavedFilterChoice,
} from "./SavedFilterDialogs";

interface DimensionPageState {
  items: FilterOption[];
  total: number;
  page: number;
  loading: boolean;
  loadingMore: boolean;
}

const emptyPage = (): DimensionPageState => ({
  items: [],
  total: 0,
  page: 0,
  loading: false,
  loadingMore: false,
});

export interface RegulationFilterPanelProps {
  applied: FilterSelectionState;
  appliedSavedFilterId?: number | null;
  savedFilters?: SavedFilterChoice[];
  canManageSavedFilters?: boolean;
  onApply: (next: FilterSelectionState, savedFilterId: number | null) => void;
  onClearApplied: () => void;
  onCreateSavedFilter?: (
    name: string,
    selection: FilterSelectionState,
  ) => Promise<{ error: string | null; id: number | null }>;
  onUpdateSavedFilter?: (
    id: number,
    name: string,
    selection: FilterSelectionState,
  ) => Promise<string | null>;
  onDeleteSavedFilter?: (id: number) => Promise<void>;
  onReorderSavedFilters?: (orderedIds: number[]) => void;
}

const FILTER_SEARCH_DEBOUNCE_MS = 300;

function valuesQueryFor(dimension: FilterDimension, query: string): string | undefined {
  const needle = query.trim();
  if (!needle) return undefined;
  if (dimension.displayName.toLowerCase().includes(needle.toLowerCase())) {
    return undefined;
  }
  return needle;
}

export const RegulationFilterPanel: React.FC<RegulationFilterPanelProps> = ({
  applied,
  appliedSavedFilterId = null,
  savedFilters = [],
  canManageSavedFilters = false,
  onApply,
  onClearApplied,
  onCreateSavedFilter,
  onUpdateSavedFilter,
  onDeleteSavedFilter,
  onReorderSavedFilters,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [draft, setDraft] = useState<FilterSelectionState>(applied);
  const [query, setQuery] = useState("");
  const [dimensions, setDimensions] = useState<FilterDimension[]>([]);
  const [dimensionsLoading, setDimensionsLoading] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(
    FILTER_DIMENSION_KEYS.favorite,
  );
  const [pages, setPages] = useState<Record<string, DimensionPageState>>({});
  const [selectedSavedId, setSelectedSavedId] = useState<number | null>(
    appliedSavedFilterId,
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const queryTimerRef = useRef<number | null>(null);
  const startedPagesRef = useRef(new Set<string>());

  const appliedCount = countFilterSelections(applied);
  const catalogs = useMemo(() => {
    const next: Record<string, FilterOption[]> = {};
    for (const [key, page] of Object.entries(pages)) next[key] = page.items;
    return next;
  }, [pages]);
  const orderedDimensions = useMemo(
    () => orderFilterDimensions(dimensions, query, catalogs),
    [dimensions, query, catalogs],
  );
  const noMatch =
    query.trim().length > 0 &&
    orderedDimensions.length === 0 &&
    !dimensionsLoading &&
    !Object.values(pages).some((page) => page.loading);
  const activeDimension =
    orderedDimensions.find((dimension) => dimension.key === activeKey) ??
    orderedDimensions[0] ??
    null;
  const optionsByKey = useMemo(() => {
    const next: Record<string, FilterOption[]> = {};
    for (const dimension of orderedDimensions) {
      const page = pages[dimension.key];
      const selected = draft.selections[dimension.key] ?? [];
      const pinned = pinSelectedOptions(page?.items ?? [], selected, query, {
        selection: dimension.selection,
        preserveSelectionOrder: dimension.key === FILTER_DIMENSION_KEYS.tags,
        includeMissing: true,
      });
      next[dimension.key] = visibleValueOptions(dimension, pinned, query);
    }
    return next;
  }, [orderedDimensions, pages, draft.selections, query]);
  const selectedSaved =
    savedFilters.find((filter) => filter.id === selectedSavedId) ?? null;
  const savedDirty =
    selectedSaved != null &&
    !filterSelectionsEqual(draft, selectedSaved.selection);
  const draftCount = countFilterSelections(draft);
  const showSaveAsNew = canManageSavedFilters && draftCount > 0;
  const showSaveChanges =
    canManageSavedFilters && selectedSaved != null && savedDirty;

  const pageToken = (
    dimensionKey: string,
    page: number,
    q: string | undefined,
  ) => `${dimensionKey}:${page}:${(q ?? "").trim()}`;

  const clearDimensionPageTokens = (dimensionKey: string) => {
    for (const token of [...startedPagesRef.current]) {
      if (token.startsWith(`${dimensionKey}:`)) {
        startedPagesRef.current.delete(token);
      }
    }
  };

  const loadPage = async (
    dimension: FilterDimension,
    page: number,
    append: boolean,
    q = "",
  ) => {
    const queryKey = valuesQueryFor(dimension, q);
    const token = pageToken(dimension.key, page, queryKey);
    if (startedPagesRef.current.has(token)) return;
    if (!append) clearDimensionPageTokens(dimension.key);
    startedPagesRef.current.add(token);
    setPages((current) => ({
      ...current,
      [dimension.key]: {
        ...(current[dimension.key] ?? emptyPage()),
        loading: !append,
        loadingMore: append,
      },
    }));
    try {
      const result: FilterValuesPage = await loadFilterDimensionValues(
        dimension,
        {
          q: queryKey,
          page,
          pageSize: FILTER_VALUES_PAGE_SIZE,
        },
      );
      setPages((current) => {
        const previous = current[dimension.key] ?? emptyPage();
        if (append && previous.page > 0 && result.page !== previous.page + 1) {
          return {
            ...current,
            [dimension.key]: {
              ...previous,
              loading: false,
              loadingMore: false,
            },
          };
        }
        return {
          ...current,
          [dimension.key]: {
            items: append
              ? mergeFilterOptions(previous.items, result.items)
              : result.items,
            total: result.total,
            page: result.page,
            loading: false,
            loadingMore: false,
          },
        };
      });
    } catch {
      startedPagesRef.current.delete(token);
      setPages((current) => {
        const previous = current[dimension.key] ?? emptyPage();
        return {
          ...current,
          [dimension.key]: {
            ...previous,
            loading: false,
            loadingMore: false,
          },
        };
      });
    }
  };

  const scheduleValueReload = (nextQuery: string) => {
    const currentDimensions = dimensions;
    if (queryTimerRef.current != null) window.clearTimeout(queryTimerRef.current);
    queryTimerRef.current = window.setTimeout(() => {
      for (const dimension of currentDimensions) {
        void loadPage(dimension, 1, false, nextQuery);
      }
    }, FILTER_SEARCH_DEBOUNCE_MS);
  };

  const loadMoreActiveValues = () => {
    if (!activeDimension) return;
    const page = pages[activeDimension.key];
    if (!page || page.loading || page.loadingMore || page.items.length >= page.total) {
      return;
    }
    const nextPage = page.page + 1;
    const queryKey = valuesQueryFor(activeDimension, query);
    if (startedPagesRef.current.has(pageToken(activeDimension.key, nextPage, queryKey))) {
      return;
    }
    void loadPage(activeDimension, nextPage, true, query);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (queryTimerRef.current != null) window.clearTimeout(queryTimerRef.current);
    startedPagesRef.current.clear();
    setPages({});
    setAnchorEl(event.currentTarget);
    setDraft(applied);
    setQuery("");
    setSelectedSavedId(appliedSavedFilterId);
    setDimensionsLoading(true);
    void loadFilterDimensions().then((nextDimensions) => {
      setDimensions(nextDimensions);
      setDimensionsLoading(false);
      const favorite = nextDimensions.find(
        (dimension) => dimension.key === FILTER_DIMENSION_KEYS.favorite,
      );
      const initial = favorite ?? nextDimensions[0] ?? null;
      setActiveKey(initial?.key ?? null);
      for (const dimension of nextDimensions) {
        void loadPage(dimension, 1, false);
      }
    });
  };

  const applyAndClose = () => {
    onApply(draft, selectedSavedId);
    setAnchorEl(null);
  };

  const handleClose = () => {
    applyAndClose();
  };

  const pickSavedFilter = (id: number | null) => {
    setSelectedSavedId(id);
    if (id == null) {
      setDraft(emptyFilterSelection());
      return;
    }
    const chosen = savedFilters.find((filter) => filter.id === id);
    if (chosen) setDraft(chosen.selection);
  };

  return (
    <>
      <Button
        type="button"
        aria-expanded={open}
        onClick={handleOpen}
        sx={{
          height: 36,
          px: "12px",
          gap: "8px",
          minWidth: 0,
          textTransform: "none",
          border: "none",
          borderRadius: "4px",
          bgcolor: "#F2F2F2",
          color: "#101828",
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: "nowrap",
          boxShadow: "none",
          "&:hover": { bgcolor: "#EBEBEB", boxShadow: "none" },
        }}
      >
        <InsightFilterIcon sx={{ fontSize: 16 }} />
        Filters
        {appliedCount > 0 && (
          <Box
            component="span"
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              bgcolor: "#295EF1",
              color: "#fff",
              fontWeight: 700,
              fontSize: 12,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            {appliedCount}
          </Box>
        )}
        <ChevronDownIcon
          sx={{
            width: 14,
            height: 14,
            fontSize: 14,
            color: "#0D0D0D",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 551,
              maxWidth: "calc(100vw - 32px)",
              mt: 1,
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "calc(100vh - 120px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              pt: 1.5,
              pb: 1,
              flex: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: 160,
                height: 32,
                px: 1.5,
                flex: "none",
                border: "1px solid #00000010",
                borderRadius: "3.6px",
                boxSizing: "border-box",
              }}
            >
              <InsightSearchIcon sx={{ fontSize: 14, color: "#8C8C8C", flex: "none" }} />
              <Box
                component="input"
                placeholder="Search dimension or value"
                value={query}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const nextQuery = event.target.value;
                  setQuery(nextQuery);
                  scheduleValueReload(nextQuery);
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  bgcolor: "transparent",
                  fontSize: 12,
                  color: "#0D0D0D",
                  fontFamily: "inherit",
                  p: 0,
                }}
              />
              {query.length > 0 && (
                <Box
                  component="button"
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery("");
                    scheduleValueReload("");
                  }}
                  sx={{
                    border: 0,
                    bgcolor: "transparent",
                    p: 0,
                    m: 0,
                    cursor: "pointer",
                    color: "#A6A6A6",
                    fontSize: 11,
                    lineHeight: 1,
                    flex: "none",
                    fontFamily: "inherit",
                  }}
                >
                  ✕
                </Box>
              )}
            </Box>
            {canManageSavedFilters && (
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ position: "relative", width: 150, flex: "none" }}>
                  <Select
                    size="small"
                    displayEmpty
                    value={selectedSavedId ?? 0}
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      pickSavedFilter(value === 0 ? null : value);
                    }}
                    renderValue={(value) => {
                      const label = !value
                        ? "Select saved filter"
                        : (savedFilters.find((filter) => filter.id === value)
                            ?.name ?? "Select saved filter");
                      return (
                        <Box
                          component="span"
                          sx={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                          }}
                        >
                          {label}
                        </Box>
                      );
                    }}
                    IconComponent={SelectChevronIcon}
                    sx={{
                      width: 150,
                      height: 32,
                      fontSize: 12,
                      borderRadius: "3.6px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#00000010",
                      },
                      "& .MuiSelect-icon": {
                        width: 8,
                        height: 5,
                        top: "50%",
                        transform: "translateY(-50%)",
                        right: 12,
                        color: "#A6A6A6",
                      },
                      "& .MuiSelect-select": {
                        display: "block",
                        py: 0,
                        pl: "12px",
                        pr: selectedSavedId != null ? "48px !important" : "28px !important",
                        lineHeight: "30px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value={0}>Select saved filter</MenuItem>
                    {savedFilters.map((filter) => (
                      <MenuItem key={filter.id} value={filter.id}>
                        {filter.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedSavedId != null && (
                    <Box
                      component="button"
                      type="button"
                      aria-label="Clear saved filter"
                      title="Clear"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        pickSavedFilter(null);
                      }}
                      sx={{
                        position: "absolute",
                        right: 28,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 12,
                        height: 12,
                        p: 0,
                        border: 0,
                        bgcolor: "transparent",
                        color: "#A6A6A6",
                        fontSize: 11,
                        lineHeight: 1,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        "&:hover": { bgcolor: "transparent" },
                      }}
                    >
                      ✕
                    </Box>
                  )}
                </Box>
                {showSaveChanges && (
                  <Tooltip title="Save changes">
                    <IconButton
                      aria-label="Save changes"
                      onClick={() => {
                        if (!selectedSaved || !onUpdateSavedFilter) return;
                        void onUpdateSavedFilter(
                          selectedSaved.id,
                          selectedSaved.name,
                          draft,
                        );
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        border: "1px solid #0000000F",
                        borderRadius: "8px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: "#F5F7FB" },
                      }}
                    >
                      <SaveChangesIcon sx={{ fontSize: 16, color: "#0A0A0A" }} />
                    </IconButton>
                  </Tooltip>
                )}
                {showSaveAsNew && (
                  <Tooltip title="Save as new">
                    <IconButton
                      aria-label="Save as new"
                      onClick={() => {
                        setSaveError(null);
                        setSaveOpen(true);
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        border: "1px solid #0000000F",
                        borderRadius: "8px",
                        bgcolor: "#fff",
                        "&:hover": { bgcolor: "#F5F7FB" },
                      }}
                    >
                      <SaveNewIcon sx={{ fontSize: 16, color: "#0A0A0A" }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Manage saved filters">
                  <IconButton
                    aria-label="Manage saved filters"
                    onClick={() => setManageOpen(true)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: "1px solid #0000000F",
                      borderRadius: "8px",
                      bgcolor: "#fff",
                      "&:hover": { bgcolor: "#F5F7FB" },
                    }}
                  >
                    <InsightSettingsIcon sx={{ fontSize: 16, color: "#0A0A0A" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          <Box sx={{ position: "relative", flex: 1, minHeight: 166, overflow: "hidden" }}>
            {dimensionsLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.6)",
                  zIndex: 1,
                }}
              >
                <CircularProgress size={22} />
              </Box>
            )}
            <FilterCriteriaEditor
              dimensions={orderedDimensions}
              draft={draft}
              activeKey={activeDimension?.key ?? activeKey}
              onActiveKeyChange={(key) => {
                setActiveKey(key);
                const dimension = dimensions.find((item) => item.key === key);
                if (dimension && !pages[key]) {
                  void loadPage(dimension, 1, false);
                }
              }}
              onDraftChange={setDraft}
              optionsByKey={optionsByKey}
              onReachEnd={loadMoreActiveValues}
              loadingMore={Boolean(
                activeDimension && pages[activeDimension.key]?.loadingMore,
              )}
              leftEmptyMessage={
                noMatch ? "No matching dimension or value" : null
              }
              maxHeight={280}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "20px",
              py: "14px",
              borderTop: "1px solid rgba(0,0,0,0.06)",
              bgcolor: "#fff",
              flex: "none",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => {
                setDraft(emptyFilterSelection());
                setSelectedSavedId(null);
                onClearApplied();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                border: 0,
                bgcolor: "transparent",
                cursor: "pointer",
                color: "#E30000",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "inherit",
                p: 0,
              }}
            >
              <ClearFilterIcon sx={{ fontSize: 16, color: "#E30000" }} />
              Clear filter
            </Box>
            <IconButton
              aria-label="Apply"
              onClick={applyAndClose}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "6px",
                bgcolor: "#295EF1",
                color: "#fff",
                "&:hover": { bgcolor: "#1F4FD6" },
              }}
            >
              <ApplyCheckIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Popover>
      {saveOpen && (
        <SaveFilterNameDialog
          open
          error={saveError}
          onClose={() => setSaveOpen(false)}
          onSave={(name) => {
            if (!onCreateSavedFilter) {
              setSaveOpen(false);
              return;
            }
            return onCreateSavedFilter(name, draft).then((result) => {
              if (result.error) {
                setSaveError(result.error);
                return;
              }
              setSaveError(null);
              setSaveOpen(false);
              if (result.id != null) setSelectedSavedId(result.id);
            });
          }}
        />
      )}
      {manageOpen && (
        <ManageSavedFiltersDialog
          open
          filters={savedFilters}
          onClose={() => setManageOpen(false)}
          onSave={async (id, name, selection) => {
            const error =
              (await onUpdateSavedFilter?.(id, name, selection)) ?? null;
            if (!error && id === selectedSavedId) setDraft(selection);
            return error;
          }}
          onDelete={async (id) => {
            await onDeleteSavedFilter?.(id);
            if (selectedSavedId === id) {
              setSelectedSavedId(null);
              setDraft(emptyFilterSelection());
            }
          }}
          onReorder={(orderedIds) => onReorderSavedFilters?.(orderedIds)}
        />
      )}
    </>
  );
};
